import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { RiMicAiFill } from "react-icons/ri";
import { PiMapPinAreaFill } from "react-icons/pi";
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import { useProductStore } from '@/store/productStore';
import { useCouponStore } from '@/store/couponStore';
import { useCarouselStore } from '@/store/carouselStore';
import { supabase } from '@/utils/supabase';
import { ProductCard } from '@/components/ProductCard';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Search, ChevronDown, SlidersHorizontal, Download, MessageCircle } from 'lucide-react';
import { ShopAwning } from '@/components/ShopAwning';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { HomeCarousel } from '@/components/HomeCarousel';
import { InstallPromptDialog } from '@/components/InstallPromptDialog';
import {
  RiMapPinLine,
  RiArrowRightSLine,
  RiCompass3Line,
  RiAddCircleLine
} from "react-icons/ri";
import { getLocationName } from '@/data/bangladesh-locations';

export function AppHome() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();
  const { profile, user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const { products, categories, fetchProducts, isLoading, hasMore, currentPage } = useProductStore();
  const { fetchCoupons } = useCouponStore();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  const [sortBy, setSortBy] = useState('newest');
  const [activeTab, setActiveTab] = useState('All');
  const [isListening, setIsListening] = useState(false);
  const [isLocationDrawerOpen, setIsLocationDrawerOpen] = useState(false);
  const { slides, isLoading: isCarouselLoading, fetchSlides, initRealtimeSubscription, cleanupRealtimeSubscription } = useCarouselStore();
  const [isInstalled] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(display-mode: standalone)').matches : false
  );



  const defaultAddress = useMemo(() => {
    if (!profile?.address) return null;
    if (Array.isArray(profile.address)) {
      return (profile.address as Record<string, unknown>[]).find(a => a.is_default) || profile.address[0];
    }
    return profile.address as Record<string, unknown>;
  }, [profile]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const timeoutRef = useRef<number | null>(null);

  const activeFiltersCount = [
    searchQuery,
    selectedCategory && selectedCategory !== 'all',
    priceRange[0] > 0 || priceRange[1] < 500,
  ].filter(Boolean).length;

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);

    if (tab === 'All' || tab === 'Newest') {
      fetchProducts({ limit: 6, sort: 'newest' });
    } else if (tab === 'Popular') {
      fetchProducts({ limit: 6, isFeatured: true });
    } else if (tab === 'Gift') {
      fetchProducts({ limit: 6, category: 'gift' });
    }
  };


  const startVoiceSearch = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(currentLang === 'bn' ? 'à¦†à¦ªà¦¨à¦¾à¦° à¦¬à§à¦°à¦¾à¦‰à¦œà¦¾à¦° à¦­à¦¯à¦¼à§‡à¦¸ à¦¸à¦¾à¦°à§à¦š à¦¸à¦¾à¦ªà§‹à¦°à§à¦Ÿ à¦•à¦°à§‡ à¦¨à¦¾' : 'Your browser does not support voice search');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = currentLang === 'bn' ? 'bn-BD' : 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsListening(true);
      timeoutRef.current = window.setTimeout(() => {
        recognition.stop();
      }, 8000);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      const transcript = event.results[event.results.length - 1][0].transcript;
      setSearchQuery(transcript);
      recognition.stop();

      // Auto-redirect on voice result
      const params = new URLSearchParams();
      params.set('search', transcript);
      navigate(`/products?${params.toString()}`);
    };
    recognition.onerror = () => { if (timeoutRef.current !== null) clearTimeout(timeoutRef.current); setIsListening(false); };
    recognition.onend = () => { if (timeoutRef.current !== null) clearTimeout(timeoutRef.current); setIsListening(false); };

    recognition.start();
  }, [isListening, currentLang, navigate]);

  useEffect(() => {
    fetchProducts({ limit: 12, sort: 'newest' });
    fetchCoupons();
    fetchSlides();
    initRealtimeSubscription();

    return () => {
      cleanupRealtimeSubscription();
    };
  }, [fetchProducts, fetchCoupons, fetchSlides, initRealtimeSubscription, cleanupRealtimeSubscription]);

  useEffect(() => {

    // Connectivity Diagnostic
    if (import.meta.env.DEV) {
      const runDiagnostic = async () => {
        if (import.meta.env.DEV) console.log('--- Connectivity Diagnostic Started ---');
        const result = await supabase.from('products').select('id', { count: 'exact', head: true });

        if (result && !result.error) {
          if (import.meta.env.DEV) console.log('âœ… Supabase Connected');
          if (import.meta.env.DEV) console.log('Products found in DB:', result.data?.length || 0);
        } else {
          console.error('âŒ Supabase Connection Error:', result?.error);
          if (result?.error?.message?.includes('column')) {
            console.error('ðŸ‘‰ Tip: It looks like your database schema is out of sync. Please run the SQL migration script.');
          }
        }
      };
      runDiagnostic();
    }
  }, []);

  const handleSearch = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);

    // Maintain other active filters if any
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 500) params.set('maxPrice', priceRange[1].toString());

    navigate(`/products?${params.toString()}`);
  };

  const handleApplyFilters = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 500) params.set('maxPrice', priceRange[1].toString());
    if (sortBy && sortBy !== 'newest') params.set('sort', sortBy);

    navigate(`/products?${params.toString()}`);
    // setShowFilters(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setPriceRange([0, 500]);
    // Optionally redirect to base products or just clear local state
    // navigate('/products'); 
  };

  const homeCategories = [
    { id: 'kunafa', name: 'Kunafa', image: '/images/products/royel_pistachio_kunafa.png' },
    { id: 'dark_chocolate', name: 'Dark Choco', image: '/images/products/nutella_bar.png' },
    { id: 'truffles', name: 'Truffles', image: '/images/products/signature_truffles.jpg' },
    { id: 'gift_boxes', name: 'Gift Boxes', image: '/images/products/mini_kunafa_box.png' },
  ];

  // Use real products directly (since tab clicks navigate to /products anyway)
  const filteredProducts = products;

  return (
    <div className="min-h-[100dvh] bg-white font-sans">
      <div className="animate-fade-in pb-24 lg:pb-10">
        <div className="chocolate-beveled rounded-none mb-0 relative z-20 !shadow-none">
          {/* Sexy Background Pattern Overlay 
          <div className="absolute inset-0 opacity-[0.25] bg-vegetable-pattern pointer-events-none" />*/}

          {/* Top Header: Location & Notifications */}
          <header className="px-5 pt-5 pb-2 flex items-center justify-between relative z-10">
            <Drawer open={isLocationDrawerOpen} onOpenChange={setIsLocationDrawerOpen}>
              <DrawerTrigger asChild>
                <div className="flex flex-col cursor-pointer">
                  <span className="text-white text-[10px] mb-0.5 font-medium uppercase tracking-wider">{t('location')}</span>
                  <div className="flex items-center gap-1.5 mt-0 group">
                    <PiMapPinAreaFill className="w-5 h-5 text-white" />
                    <span className="font-semibold text-[15px] text-white truncate max-w-full ">
                      {defaultAddress?.area && defaultAddress?.city ? `${getLocationName(defaultAddress.area as string, currentLang)}, ${getLocationName(defaultAddress.city as string, currentLang)}` : (currentLang === 'bn' ? 'à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦¸à§‡à¦Ÿ à¦•à¦°à§à¦¨' : 'Set Address')}
                    </span>
                    <ChevronDown className="w-4 h-4 text-white transition-transform group-hover:translate-y-0.5" />
                  </div>
                </div>
              </DrawerTrigger>
              <DrawerContent className="rounded-t-[32px] border-none bg-white/95 backdrop-blur-xl max-h-[85vh]">
                <div className="mx-auto w-12 h-1.5 bg-gray-200 rounded-full mt-3 mb-6" />
                <DrawerHeader className="px-6 pt-0">
                  <DrawerTitle className="text-2xl font-black text-black flex items-center gap-2">
                    <RiMapPinLine className="text-primary" />
                    {currentLang === 'bn' ? 'à¦¡à§‡à¦²à¦¿à¦­à¦¾à¦°à¦¿ à¦²à§‹à¦•à§‡à¦¶à¦¨' : 'Delivery Location'}
                  </DrawerTitle>
                  <DrawerDescription className="text-gray-700 font-medium">
                    {currentLang === 'bn' ? 'à¦†à¦ªà¦¨à¦¾à¦° à¦¬à¦°à§ à¦¤à¦®à¦¾à¦¨ à¦¡à§‡à¦²à¦¿à¦­à¦¾à¦°à¦¿ à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦¨à¦¿à¦°à§ à¦¬à¦¾à¦šà¦¨ à¦•à¦°à§ à¦¨' : 'Select your current delivery address'}
                  </DrawerDescription>
                </DrawerHeader>

                <div className="px-6 py-4 space-y-4">
                  {/* Active Address Card */}
                  <div className="p-5 bg-primary/5 border border-primary/10 rounded-3xl flex items-center justify-between group transition-all active:scale-[0.98]">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                        <RiCompass3Line className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-black leading-tight">
                          {currentLang === 'bn' ? 'à¦¬à¦°à§ à¦¤à¦®à¦¾à¦¨ à¦ à¦¿à¦•à¦¾à¦¨à¦¾' : 'Current Address'}
                        </h4>
                        <p className="text-sm text-gray-800 mt-0.5 overflow-hidden">
                          {defaultAddress?.street && defaultAddress?.city ? `${defaultAddress.street as string}, ${getLocationName(defaultAddress.city as string, currentLang)}` : (currentLang === 'bn' ? 'à¦•à§‹à¦¨à§‹ à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦¸à§‡à¦Ÿ à¦•à¦°à¦¾ à¦¹à¦¯à¦¼à¦¨à¦¿' : 'No address set')}
                        </p>
                      </div>
                    </div>
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <button
                    onClick={() => {
                      setIsLocationDrawerOpen(false);
                      navigate('/address');
                    }}
                    className="w-full p-5 bg-gray-50 hover:bg-gray-100 rounded-3xl flex items-center justify-between group transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white shadow-sm rounded-2xl flex items-center justify-center text-gray-600 group-hover:text-primary transition-colors">
                        <RiAddCircleLine className="w-6 h-6" />
                      </div>
                      <span className="font-bold text-gray-800">
                        {currentLang === 'bn' ? 'à¦¨à¦¤à§ à¦¨ à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦¯à§‹à¦— à¦•à¦°à§ à¦¨' : 'Add New Address'}
                      </span>
                    </div>
                    <RiArrowRightSLine className="w-6 h-6 text-gray-600 group-hover:text-gray-700 transition-colors" />
                  </button>
                </div>

                <DrawerFooter className="px-6 pb-10 pt-2 text-center">
                  <Button
                    variant="link"
                    className="text-primary font-bold h-auto p-0"
                    onClick={() => {
                      setIsLocationDrawerOpen(false);
                      navigate('/address');
                    }}
                  >
                    {currentLang === 'bn' ? 'à¦¸à¦¬ à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦¦à§‡à¦–à§ à¦¨' : 'Manage All Addresses'}
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>

            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="premium" />
              
              {user && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20 text-white rounded-full"
                  onClick={() => navigate('/chat')}
                >
                  <MessageCircle className="h-5 w-5" />
                </Button>
              )}

              {!isInstalled && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20 text-white rounded-full"
                  onClick={() => window.dispatchEvent(new Event('show-install-prompt'))}
                >
                  <Download className="h-5 w-5" />
                </Button>
              )}
            </div>
          </header>

          {/* Search Bar & Filter */}
          <section className="px-4 pb-0 flex items-center gap-3 relative z-10">
            <div className="bg-transparent rounded-xl p-0 w-full">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search and Mobile Filter */}
                <div className="flex flex-1 gap-2">
                  <form onSubmit={handleSearch} className="flex-1">
                    <div className="relative">
                      <Search onClick={handleSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600 cursor-pointer hover:text-primary transition-colors" />
                      <Input
                        type="search"
                        placeholder={t('search.placeholder')}
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10 !border-none !outline-none !ring-0 focus-visible:!ring-0 focus-visible:!border-none shadow-[1px_2px_4px_rgba(0,0,0,.1)] bg-white text-black placeholder:text-gray-600 rounded-sm"
                      />
                      <button
                        type="button"
                        onClick={startVoiceSearch}
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all ${isListening
                          ? 'text-primary'
                          : 'text-primary bg-white lg:bg-transparent lg:hover:bg-white'
                          }`}
                      >
                        {isListening && (
                          <>
                            <style>{`
                              @keyframes spark {
                                0%, 100% { opacity: 0; transform: scale(0); }
                                50% { opacity: 1; transform: scale(1); }
                              }
                            `}</style>
                            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
                              <span
                                key={deg}
                                className="absolute w-1 h-1 rounded-full bg-primary"
                                style={{
                                  top: `calc(50% - 2px + ${Math.sin(deg * Math.PI / 180) * 14}px)`,
                                  left: `calc(50% - 2px + ${Math.cos(deg * Math.PI / 180) * 14}px)`,
                                  transform: 'translate(-50%, -50%)',
                                  animation: `spark 1.2s ease-in-out ${i * 0.15}s infinite`,
                                }}
                              />
                            ))}
                          </>
                        )}
                        <RiMicAiFill className="h-4 w-4 relative z-10" />
                      </button>
                    </div>
                  </form>
                </div>

                {/* Desktop Filters */}
                <div className="hidden lg:flex items-center gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="gap-2 border-none shadow-[1px_2px_4px_rgba(0,0,0,.2),-1px_2px_4px_rgba(0,0,0,.2)] rounded-sm">
                        <SlidersHorizontal className="h-4 w-4" />
                        {t('btn.filter')}
                        {activeFiltersCount > 0 && (
                          <Badge variant="secondary" className="ml-1">
                            {activeFiltersCount}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[600px] p-6 shadow-xl" align="center">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Category Filter */}
                        <div>
                          <label className="text-sm font-medium mb-1 block text-gray-700">
                            {t('filter.category')}
                          </label>
                          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('filter.category')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">{t('btn.clear')}</SelectItem>
                              {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {t(`categories.${cat}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Sort */}
                        <div>
                          <label className="text-sm font-medium mb-1 block text-gray-700">
                            {t('filter.sort')}
                          </label>
                          <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('filter.sort')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="newest">{t('filter.sort.newest')}</SelectItem>
                              <SelectItem value="price_low">{t('filter.sort.price_low')}</SelectItem>
                              <SelectItem value="price_high">{t('filter.sort.price_high')}</SelectItem>
                              <SelectItem value="name">{t('filter.sort.name')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Price Range - full width row */}
                      <div className="mt-6">
                        <label className="text-sm font-medium mb-3 block text-gray-700">
                          {t('filter.price')}: à§³{priceRange[0]} - à§³{priceRange[1]}
                        </label>
                        <div className="pt-2 px-2">
                          <Slider
                            value={priceRange}
                            onValueChange={(value) => setPriceRange(value as [number, number])}
                            max={500}
                            step={10}
                          />
                        </div>
                      </div>

                      <div className="mt-8 pt-4 border-none flex justify-between gap-3">
                        <Button onClick={clearFilters} variant="outline" className="flex-1">
                          {t('btn.clear')}
                        </Button>
                        <Button onClick={handleApplyFilters} className="flex-1">
                          {t('btn.apply')}
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Active Filters 
              {activeFiltersCount > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-none">
                  {searchQuery && (
                    <Badge variant="secondary" className="gap-1">
                      {t('product.searched')}: {searchQuery}
                      <button type="button" onClick={() => setSearchQuery('')} className="ml-1">
                        <X className="h-3 w-3 cursor-pointer" />
                      </button>
                    </Badge>
                  )}
                  {selectedCategory && selectedCategory !== 'all' && (
                    <Badge variant="secondary" className="gap-1">
                      {t('filter.category')}: {t(`categories.${selectedCategory}`)}
                      <button type="button" onClick={() => setSelectedCategory('all')} className="ml-1">
                        <X className="h-3 w-3 cursor-pointer" />
                      </button>
                    </Badge>
                  )}
                  {(priceRange[0] > 0 || priceRange[1] < 500) && (
                    <Badge variant="secondary" className="gap-1">
                      à§³{priceRange[0]} - à§³{priceRange[1]}
                      <button type="button" onClick={() => setPriceRange([0, 500])} className="ml-1">
                        <X className="h-3 w-3 cursor-pointer" />
                      </button>
                    </Badge>
                  )}
                </div>
              )}
              */}
            </div>
          </section>

          {/* Shop Awning Partition */}
          <ShopAwning />
        </div>

        {/* Home Carousel Slider */}
        <HomeCarousel slides={slides} isLoading={isCarouselLoading} />

        {/* Category Section */}
        <section className="mb-2">
          <div className="px-4 flex items-center justify-between mb-2">
            <h2 className="text-lg font-black text-black flex items-center">
              {t('category')}
            </h2>
          </div>

          <div className="flex pt-1 px-4 gap-2 md:gap-3 overflow-x-auto pb-3 scrollbar-none no-scrollbar justify-center">
            {homeCategories.map((cat) => (
              <Link key={cat.id} to={`/products?category=${cat.id}`} className="flex flex-col items-center justify-start gap-2 md:gap-3 shrink-0 group">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-gray-50 flex items-center justify-center transition-all group-hover:-translate-y-1 active:scale-95 shadow-sm border border-gray-100 overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-[11px] md:text-xs font-bold text-black tracking-tight group-hover:text-primary transition-colors text-center w-full leading-tight">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>



        {/* Recommended For You Section */}
        <section className="mb-3">
          <div className="flex items-center justify-between px-4 mb-3">
            <h2 className="text-lg font-black text-black">{t('recommended_for_you')}</h2>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-4 no-scrollbar scrollbar-none px-4">
            {['All', 'Newest', 'Popular', 'Gift Box'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`px-5 h-9 rounded-full text-xs font-bold transition-all whitespace-nowrap active:scale-95 ${activeTab === tab
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : 'bg-white border border-gray-100 text-gray-700 hover:bg-gray-125'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 reduce gap-x-2 gap-y-6 px-2">
            {isLoading && products.length === 0 ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-[28px] animate-pulse" />
              ))
            ) : (
              filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => fetchProducts({
                  limit: 6,
                  page: currentPage + 1,
                  append: true,
                  sort: activeTab === 'Newest' || activeTab === 'All' ? 'newest' : undefined,
                  isFeatured: activeTab === 'Popular' ? true : undefined,
                  category: activeTab === 'Gift' ? 'gift' : undefined
                })}
                className="rounded-full px-8 font-bold text-xs h-10 border-gray-200 hover:bg-gray-50 active:scale-95 transition-all"
              >
                {isLoading ? (currentLang === 'bn' ? 'à¦²à§‹à¦¡ à¦¹à¦šà§à¦›à§‡...' : 'Loading...') : (currentLang === 'bn' ? 'à¦†à¦°à§‹ à¦¦à§‡à¦–à§à¦¨' : 'Load More')}
              </Button>
            </div>
          )}
        </section>
      </div>

      <InstallPromptDialog />
    </div>
  );
}
